import { createContext, useState, useEffect, useContext, useRef } from 'react';
import { QueryFactory } from 'services/api';
import {
  DocumentDto,
  RefDocumentEditingUserDto,
} from 'services/api/api-client.types.ts';
import { UseQueryResult } from '@tanstack/react-query';
import { currentUserIsMagadanEditor } from '../components/documentLock/editingLockHelpers.ts';

export type DocumentProviderProps = {
  documentId?: number | null;
};

export const DocumentProvider = (
  props: DocumentProviderProps & React.PropsWithChildren,
) => {
  const documentQuery = QueryFactory.DocumentQuery.useGetQuery(
    props.documentId!,
    {
      enabled: !!props.documentId,
    },
  );
  const editingUserQuery = QueryFactory.EditingLockQuery.useCheckLockQuery(
    props.documentId!,
    {
      enabled: !!props.documentId,
    },
  );

  const [documentState, setDocumentState] = useState<DocumentState>({
    hasChanges: false,
    needToSave: false,
    enableDocumentUpdate: true,
  });
  const [latestContent, setLatestContent] = useState<string | null>(null);

  // Value for this ref is set in useDocumentVariables.ts.
  const applyVariablesRef = useRef<(variableIds: number[]) => Promise<void>>(
    null!,
  );

  useEffect(() => {
    if (documentQuery.data) {
      if (!latestContent) {
        setLatestContent(documentQuery.data.content);
        return;
      }

      if (
        !currentUserIsMagadanEditor(
          editingUserQuery.data?.sessionIdentifier,
          editingUserQuery.data?.editingType,
        ) &&
        documentQuery.data.content !== latestContent
      ) {
        setLatestContent(documentQuery.data.content);
        setDocumentState((prevState) => ({
          ...prevState,
          hasChanges: true,
        }));
      }
    }
  }, [
    documentQuery.data?.content,
    editingUserQuery.data?.sessionIdentifier,
    props.documentId,
  ]);

  return (
    <DocumentContext.Provider
      value={{
        documentState,
        setDocumentState,
        documentQuery,
        editingUserQuery,
        applyVariablesRef,
      }}
    >
      {props.children}
    </DocumentContext.Provider>
  );
};

export const useDocumentContext = () => {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error('useDocumentContext must be used within DocumentProvider');
  }
  return context;
};

export type DocumentState = {
  hasChanges: boolean;
  needToSave: boolean;
  enableDocumentUpdate: boolean;
};

export type DocumentContextType = {
  documentState: DocumentState;
  setDocumentState: React.Dispatch<React.SetStateAction<DocumentState>>;
  documentQuery: UseQueryResult<DocumentDto, unknown>;
  editingUserQuery: UseQueryResult<RefDocumentEditingUserDto, unknown>;
  applyVariablesRef: React.RefObject<(variableIds: number[]) => Promise<void>>;
};

export const DocumentContext = createContext<DocumentContextType | null>(null);
